import { useState, useEffect } from "react";
import { useHeaderContext } from "../../../context/headerContext";
import { useUserContext } from "../../../context/userContext";
import Button from "../../../components/Button";
import ContentCard from "../../../components/ContentCard";
import TextInput from "../../../components/TextInput";
import TextArea from "../../../components/TextArea";
import { parseISO, format } from 'date-fns';
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";
 
export default function VenueTemperature() {
  const [addVenueTempOverlay, setAddVenueTempOverlay] = useState(false);
  const [venueData, setVenueData] = useState(null);
  const [venueDataPoints, setVenueDataPoints] = useState(null);
  const [venueHistoricDataPoints, setVenueHistoricDataPoints] = useState();
  const { setTitle, setAddFunc, title, setBackButton } = useHeaderContext();
  const { selectedVenue, user, logout, updateTokens } = useUserContext();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(null);

  useEffect(() => {
    setTitle("Venue Data");
    setBackButton(true);
    setAddFunc(() => setAddVenueTempOverlay(prevState => !prevState));
  }, []);

  useEffect(() => {
    fetchVenueData();
    fetchVenueDataPoints();
  }, [title])

  useEffect(() => {
    fetchVenueHistoricDataPoints();
  }, [venueData])

  const fetchVenueData = async () => {
    await Sentry.startSpan({ name: "Fetch Venue Data", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Venue/FetchSingleVenueDirectoryListing?OrgID=${selectedVenue.OrgID}`;
        span.setAttribute("http.url", url);
  
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (response.ok) {
          const data = await response.json();
          setVenueData(data);
        } else {
          throw new Error("Failed to fetch venue data");
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { OrgID: selectedVenue.OrgID } });
        LogErrors(user, logout, updateTokens, error);
      } finally {
        span.end();
      }
    });
  };
  
  const fetchVenueDataPoints = async () => {
    await Sentry.startSpan({ name: "Fetch Venue Data Points", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}CollectableDataPoints/FetchCollectableDataPointsForVenue?OrgID=${selectedVenue.OrgID}`;
        span.setAttribute("http.url", url);
  
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (response.ok) {
          const data = await response.json();
          setVenueDataPoints(data);
        } else {
          throw new Error("Failed to fetch venue data points");
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { OrgID: selectedVenue.OrgID } });
        LogErrors(user, logout, updateTokens, error);
      } finally {
        span.end();
      }
    });
  };
  
  const fetchVenueHistoricDataPoints = async (page = 0, loadMore = false) => {
    await Sentry.startSpan({ name: "Fetch Venue Historic Data Points", op: "http.request" }, async (span) => {
      setCurrentPage(page);
      try {
        const url = `${import.meta.env.VITE_API_URL}CollectableDataPoints/FetchVenueDataPointHistory?OrgID=${selectedVenue.OrgID}&pagesize=15&page=${page}`;
        span.setAttribute("http.url", url);
  
        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });
  
        if (response.ok) {
          const data = await response.json();
  
          if (loadMore) {
            setVenueHistoricDataPoints((prev) => [...prev, ...data.datapoints]);
          } else {
            setTotalPages(Math.ceil(data?.recordcount?.recordCount / data?.recordcount?.pageSize));
            setVenueHistoricDataPoints(data.datapoints);
          }
        } else {
          throw new Error("Failed to fetch venue historic data points");
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { OrgID: selectedVenue.OrgID, page } });
        LogErrors(user, logout, updateTokens, error);
      } finally {
        span.end();
      }
    });
  };  

  // Function to handle form submission for adding venue temperatures
  const addVenueTemperatures = async (e) => {
    e.preventDefault();
    const newDetails = new FormData(e.currentTarget);
    let formData = [];

    for(const key of newDetails.keys()){
      if(key !== "notes"){
        let entryInfo = {key:'', value:''}
        entryInfo.key = key;
        entryInfo.value = newDetails.get(key);
        formData.push(entryInfo)
      }
    }
    
    try {
      await Sentry.startSpan({ name: "Set Venue Data Points", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}CollectableDataPoints/setOrganisationCollectableValues`;
        span.setAttribute("http.url", url);
        span.setAttribute("venueId", selectedVenue?.OrgID);
    
        const response = await fetchWithRefresh(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify({
            venueId: selectedVenue?.OrgID.toString(),
            dataPointValues: formData?.map((dataPoint) => ({
              dataPointId: dataPoint?.key,
              dataPointValue: dataPoint?.value,
            })),
            dataPointNotes: newDetails.get("notes"),
          }),
        }, logout, updateTokens, (message) => { toast.info(message); });
    
        if (response.ok) {
          setAddVenueTempOverlay(false);
          window.location.reload();
        } else {
          throw new Error("Failed to add venue temperatures");
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { venueId: selectedVenue?.OrgID, dataPointValues: formData } });
      LogErrors(user, logout, updateTokens, error);
    }
  }

  if (!venueData || !venueHistoricDataPoints || !venueDataPoints) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-150px)]">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="relative pb-10">
      <div className="flex flex-col">
        <div className="flex flex-col gap-y-[24px] p-6">
          <div className="relative w-full h-[197px]">
            <img
              src={venueData?.DirectoryDisplayImage}
              alt="venue image"
              className="w-full h-full object-cover rounded-[4px] border-[1px] border-solid border-[#E3E3E3]"
            />
            {venueData?.MembershipsAvailable === 1 && (
              <span className="select-none uppercase pl-[11.8px] pr-[9.5px] bg-white rounded-[4px] text-[12px] leading-[24px] tracking-[2.4px] font-semibold top-2 right-2 absolute">
                Memberships Available
              </span>
            )}
          </div>
          <div className="flex flex-col">
            <h3>{venueData?.DirectoryDisplayName}</h3>
            <p>{venueData?.VenueAddress}</p>
          </div>
          <div className="flex flex-col justify-stretch border-b border-dashed border-[#E3E3E3]" />
          <div>
            <p className="select-none uppercase bg-white rounded-[4px] text-[12px] leading-[24px] tracking-[2.4px] font-semibold mb-[12px]">
              Current Venue Data
            </p>
            <div className="grid grid-cols-2 gap-[18px] mb-[24px]">
              {venueData?.DataPoints?.map((dataPoint, index) => {
                return (
                  <ContentCard
                    icon={dataPoint?.DataPointIcon}
                    title={dataPoint?.DataPointPrefix + dataPoint?.DataPointValue + dataPoint?.DataPointSuffix}
                    desc={dataPoint?.DataPointType}
                    type="weather"
                    key={`${index}-weather`}
                  />
                )
              })}
            </div>
          </div>
          <p className="select-none uppercase bg-white rounded-[4px] text-[12px] leading-[24px] tracking-[2.4px] font-semibold">
            Previous Venue Data
          </p>
        </div>
        <div className="flex flex-col gap-y-[44px] mb-[24px] bg-sportsLightGrey py-[32px] px-[25px]">
          {venueHistoricDataPoints?.map((datapoint, index) => {
            const dateTime = datapoint?.dataPointDate + " " + datapoint?.dataPointTime;
            const date = format(parseISO(dateTime), 'dd/MM/yyyy');
            const time = format(parseISO(dateTime), 'HH:mm:ss');

            return (
              <div key={`${index}-prevTempGroup`}>
                <div className="flex flex-row gap-y-[12px] justify-between mb-[16px]">
                  <div className="flex flex-col items-start gap-y-2">
                      {datapoint?.dataPointValues?.map((temp) => {
                        return (
                          <div className="flex flex-row" key={`${temp.dataPointId}-${temp.dataPointType}`}>
                            <img className="w-[24px] h-[24px]" src={temp.dataPointIcon} alt={temp.dataPointType} />
                            <p className="text-sportsDarkGrey font-semibold ml-[10px]"> {temp.dataPointType} / {temp?.dataPointPrefix + temp?.dataPointValue + temp?.dataPointSuffix}</p>
                          </div>
                        )
                      })}
                  </div>
                  <div className="flex flex-col items-end gap-y-2 text-right">
                    <p className="text-sportsDarkGrey font-semibold ml-[10px]">{time}</p>
                    <p className="text-sportsDarkGrey font-semibold ml-[10px]">{date}</p>
                  </div>
                </div>

                {datapoint?.dataPointNotes && (
                  <div className="mt-[8px]">
                    <p className="text-sportsBlack font-semibold"> Notes: <span className="font-medium">{datapoint?.dataPointNotes}</span></p>
                  </div>
                )}

                {venueHistoricDataPoints?.length -1 !== index && (
                  <div className="flex flex-col justify-stretch border-b border-dashed border-[#E3E3E3] mt-[20px]" />
                )}
              </div>
            );
          })}
          {(totalPages - 1) > currentPage && (
            <Button onClick={() => fetchVenueHistoricDataPoints((currentPage + 1), true)}>Load More</Button>
          )}
        </div>
      </div>

      {addVenueTempOverlay && (
        <div className="absolute top-0 left-0 flex justify-center items-start w-full h-full">
          <form onSubmit={addVenueTemperatures} className="bg-white p-6 rounded w-full max-w-md flex flex-col h-full gap-y-[24px]">
            {venueDataPoints?.map((dataPoint) => {
              return (
                <TextInput 
                  label={`${dataPoint?.DataPointPrefix && " ("+dataPoint?.DataPointPrefix+") "}${dataPoint?.DataPointType}${dataPoint?.DataPointSuffix && " ("+dataPoint?.DataPointSuffix+")"}`}
                  type="text"
                  name={dataPoint?.DataPointId}
                />
              )
            })}

            <TextArea
              label="Notes"
              type="text"
              name="notes"
              placeholder="Type here"
            />
            <Button submit>Add</Button>
          </form>
        </div>
      )}
    </div>
  );
}